import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material'
import type { LessonParticipant, LessonWordGroup } from '@/types'
import { editorJsDataIsEffectivelyEmpty, type EditableHomeworkItem } from '@/utils/homework'
import { lessonWordsApi } from '@/api/lessonWords'
import { wordsApi } from '@/api/words'
import { LessonPaymentStep, type LessonPaymentStepHandle } from '@/components/billing/LessonPaymentStep'
import { HomeworkItemsEditor } from '@/components/homeworks/HomeworkItemsEditor'
import { getErrorMessage } from '@/utils/error'
import {
  isLessonWordGroupIncomplete,
  lessonWordGroupNeedsCompletion,
  lessonWordOriginal,
  lessonWordTranscription,
  lessonWordTranslation,
} from './lessonWordUtils'

type StepKind = 'payment' | 'words' | 'homework'

type WordDraft = {
  wordId: number
  text: string
  originalText: string
  translation: string
  transcription: string
}

function buildStepKinds(needsPayment: boolean, needsWordsStep: boolean): StepKind[] {
  const kinds: StepKind[] = []
  if (needsPayment) kinds.push('payment')
  if (needsWordsStep) kinds.push('words')
  kinds.push('homework')
  return kinds
}

type LessonPostFinishWizardProps = {
  open: boolean
  lessonId: number
  participants: LessonParticipant[]
  needsPayment: boolean
  homeworkItems: EditableHomeworkItem[]
  homeworkEditorNonce: number
  submittingHomework: boolean
  homeworkSaveError: string | null
  onHomeworkItemsChange: (items: EditableHomeworkItem[]) => void
  onClose: () => void
  onSaveHomework: () => Promise<void>
  onSkipHomework: () => void
  onWordsSaved?: () => void | Promise<void>
  onPaymentSaved?: () => void | Promise<void>
}

export function LessonPostFinishWizard({
  open,
  lessonId,
  participants,
  needsPayment,
  homeworkItems,
  homeworkEditorNonce,
  submittingHomework,
  homeworkSaveError,
  onHomeworkItemsChange,
  onClose,
  onSaveHomework,
  onSkipHomework,
  onWordsSaved,
  onPaymentSaved,
}: LessonPostFinishWizardProps) {
  const { t } = useTranslation()
  const paymentStepRef = useRef<LessonPaymentStepHandle>(null)
  const [loadingWords, setLoadingWords] = useState(false)
  const [wordsError, setWordsError] = useState<string | null>(null)
  const [wordGroups, setWordGroups] = useState<LessonWordGroup[]>([])
  const [wordDrafts, setWordDrafts] = useState<WordDraft[]>([])
  const [savingWords, setSavingWords] = useState(false)
  const [wordsSaveError, setWordsSaveError] = useState<string | null>(null)
  const [activeStep, setActiveStep] = useState(0)
  const [frozenStepKinds, setFrozenStepKinds] = useState<StepKind[]>(['homework'])
  const [hadPaymentStep, setHadPaymentStep] = useState(false)
  const [paymentCanSubmit, setPaymentCanSubmit] = useState(false)
  const [paymentSubmitting, setPaymentSubmitting] = useState(false)
  const wasOpenRef = useRef(false)

  const needsWordsStep = useMemo(
    () => !loadingWords && wordGroups.length > 0 && lessonWordGroupNeedsCompletion(wordGroups),
    [loadingWords, wordGroups],
  )

  const stepLabels: Record<StepKind, string> = useMemo(
    () => ({
      payment: t('lessonWords.wizard.paymentStep', 'Оплата'),
      words: t('lessonWords.wizard.wordsStep', 'Слова'),
      homework: t('lessonWords.wizard.homeworkStep', 'Домашнее задание'),
    }),
    [t],
  )

  const currentStepKind = frozenStepKinds[activeStep] ?? 'homework'

  /**
   * Есть ли что сохранять: пустые текстовые блоки отсеиваются перед отправкой,
   * поэтому «Сохранить» на пустом списке отвечало бы ошибкой валидации вместо
   * подсказки. Уйти без домашки — соседняя кнопка «Без домашки сегодня».
   */
  const hasHomeworkToSave = useMemo(
    () =>
      homeworkItems.some(
        (item) => item.type !== 'text' || !editorJsDataIsEffectivelyEmpty(item.task_json),
      ),
    [homeworkItems],
  )

  const loadWords = useCallback(async () => {
    if (!Number.isFinite(lessonId)) return
    setLoadingWords(true)
    setWordsError(null)
    try {
      const groups = await lessonWordsApi.list(lessonId)
      setWordGroups(groups)
      const incomplete = groups.filter((group) => isLessonWordGroupIncomplete(group))
      setWordDrafts(
        incomplete.map((group) => ({
          wordId: group.word_id,
          text: lessonWordOriginal(group.word),
          originalText: lessonWordOriginal(group.word),
          translation: lessonWordTranslation(group.word),
          transcription: lessonWordTranscription(group.word),
        })),
      )
    } catch (err) {
      setWordsError(getErrorMessage(err))
      setWordGroups([])
      setWordDrafts([])
    } finally {
      setLoadingWords(false)
    }
  }, [lessonId])

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setActiveStep(0)
      setHadPaymentStep(needsPayment)
      if (needsPayment) {
        setFrozenStepKinds(buildStepKinds(true, false))
      }
      void loadWords()
    }
    if (!open && wasOpenRef.current) {
      setHadPaymentStep(false)
    }
    wasOpenRef.current = open
  }, [open, needsPayment, loadWords])

  useEffect(() => {
    if (!open || loadingWords) return
    setFrozenStepKinds(buildStepKinds(hadPaymentStep, needsWordsStep))
    if (!hadPaymentStep) {
      setActiveStep(0)
    }
  }, [open, loadingWords, hadPaymentStep, needsWordsStep])

  useEffect(() => {
    if (activeStep >= frozenStepKinds.length) {
      setActiveStep(Math.max(0, frozenStepKinds.length - 1))
    }
  }, [activeStep, frozenStepKinds.length])

  const handleWordDraftChange = (
    wordId: number,
    field: 'text' | 'translation' | 'transcription',
    value: string,
  ) => {
    setWordDrafts((prev) =>
      prev.map((draft) => (draft.wordId === wordId ? { ...draft, [field]: value } : draft)),
    )
  }

  /** Данные для отправки: только заполненные поля; пустые перевод/транскрипцию можно оставить на потом. */
  const buildWordUpdatePayload = (draft: WordDraft) => {
    const payload: { word?: string; translation?: string; transcription?: string } = {}
    const text = draft.text.trim()
    if (text !== '' && text !== draft.originalText.trim()) {
      payload.word = text
    }
    if (draft.translation.trim() !== '') {
      payload.translation = draft.translation.trim()
    }
    if (draft.transcription.trim() !== '') {
      payload.transcription = draft.transcription.trim()
    }
    return payload
  }

  const handleSaveWordsAndContinue = async () => {
    setSavingWords(true)
    setWordsSaveError(null)
    try {
      await Promise.all(
        wordDrafts
          .map((draft) => ({ draft, payload: buildWordUpdatePayload(draft) }))
          .filter(({ payload }) => Object.keys(payload).length > 0)
          .map(({ draft, payload }) => wordsApi.updateWord(draft.wordId, payload)),
      )
      await onWordsSaved?.()
      setActiveStep((prev) => prev + 1)
    } catch (err) {
      setWordsSaveError(getErrorMessage(err))
    } finally {
      setSavingWords(false)
    }
  }

  const handleSkipWordsStep = () => {
    setWordsSaveError(null)
    setActiveStep((prev) => prev + 1)
  }

  const handlePaymentContinue = async () => {
    const success = await paymentStepRef.current?.submit()
    if (!success) return
    setHadPaymentStep(true)
    await onPaymentSaved?.()
    setActiveStep((prev) => prev + 1)
  }

  const showWordsSkeleton = loadingWords && !hadPaymentStep

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('lessonWords.wizard.title', 'Завершение урока')}</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {frozenStepKinds.map((kind) => (
            <Step key={kind}>
              <StepLabel>{stepLabels[kind]}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {showWordsSkeleton ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Skeleton variant="rounded" height={40} />
            <Skeleton variant="rounded" height={40} />
            <Skeleton variant="rounded" height={40} />
          </Box>
        ) : (
          <>
            {wordsError && currentStepKind === 'words' && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {wordsError}
              </Alert>
            )}

            {currentStepKind === 'payment' && (
              <LessonPaymentStep
                ref={paymentStepRef}
                lessonId={lessonId}
                participants={participants}
                enabled={open && needsPayment && activeStep === frozenStepKinds.indexOf('payment')}
                onCanSubmitChange={setPaymentCanSubmit}
                onSubmittingChange={setPaymentSubmitting}
              />
            )}

            {currentStepKind === 'words' && needsWordsStep && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t(
                    'lessonWords.wizard.wordsHint',
                    'Проверьте слова, добавленные на уроке: при необходимости исправьте слово и заполните перевод и транскрипцию. Пустые поля можно оставить и заполнить позже.',
                  )}
                </Typography>

                {wordDrafts.map((draft) => (
                  <Box
                    key={draft.wordId}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                      gap: 1.5,
                      alignItems: 'start',
                    }}
                  >
                    <TextField
                      label={t('lessonWords.form.original', 'Слово')}
                      value={draft.text}
                      onChange={(e) => handleWordDraftChange(draft.wordId, 'text', e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label={t('lessonWords.form.translation', 'Перевод')}
                      value={draft.translation}
                      onChange={(e) => handleWordDraftChange(draft.wordId, 'translation', e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label={t('lessonWords.form.transcription', 'Транскрипция')}
                      value={draft.transcription}
                      onChange={(e) => handleWordDraftChange(draft.wordId, 'transcription', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </Box>
                ))}

                {wordsSaveError && <Alert severity="error">{wordsSaveError}</Alert>}
              </Box>
            )}

            {currentStepKind === 'homework' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Тот же редактор, что и в правке домашки: мастер завершения
                  урока — главный момент, когда задание задают по разобранной
                  теме, и лишать его кнопки «Добавить задание» значит заставлять
                  учителя переоткрывать домашку сразу после сохранения */}
                <HomeworkItemsEditor
                  key={`homework-editor-${lessonId}-${homeworkEditorNonce}`}
                  items={homeworkItems}
                  onChange={onHomeworkItemsChange}
                  lessonId={lessonId}
                />
                {homeworkSaveError && (
                  <Typography color="error.main" variant="body2">
                    {homeworkSaveError}
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {currentStepKind === 'payment' ? (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => void handlePaymentContinue()}
            disabled={paymentSubmitting || !paymentCanSubmit}
            startIcon={paymentSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('lessonWords.wizard.next', 'Далее')}
          </Button>
        ) : currentStepKind === 'words' ? (
          <>
            <Button onClick={handleSkipWordsStep} disabled={savingWords || loadingWords}>
              {t('lessonWords.wizard.skipWords', 'Пропустить')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => void handleSaveWordsAndContinue()}
              disabled={savingWords || loadingWords}
              startIcon={savingWords ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {t('lessonWords.wizard.next', 'Далее')}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onSkipHomework} disabled={submittingHomework}>
              {t('homeworks.skipToday', 'Без домашки сегодня')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => void onSaveHomework()}
              disabled={submittingHomework || !hasHomeworkToSave}
            >
              {t('common.save', 'Сохранить')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
